import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  TrendingDown, ShoppingBag, UserX, ImageOff,
  Camera, Palette, Instagram, MapPin, Users2,
  QrCode, MessageSquare, RefreshCw, Mail,
  BarChart, PieChart, UserCheck, TrendingUp,
  ArrowRight, CheckCircle2, UtensilsCrossed,
  Target, Zap, BarChart3, Shield,
} from "lucide-react";

const problems = [
  { icon: TrendingDown, text: "Inconsistent daily orders" },
  { icon: ShoppingBag, text: "High dependency on Zomato/Swiggy" },
  { icon: UserX, text: "Customers don't come back" },
  { icon: ImageOff, text: "Content doesn't convert into orders" },
];

const contentEngine = [
  { icon: Camera, label: "Reels + Creative Strategy", desc: "Scroll-stopping content that drives action" },
  { icon: Palette, label: "Shoot + Edit", desc: "Professional food content that sells" },
];

const distribution = [
  { icon: Instagram, label: "Instagram Ads", desc: "Reach food lovers in your area" },
  { icon: MapPin, label: "Zomato Ads", desc: "Dominate local food search results" },
  { icon: Users2, label: "Influencer Marketing", desc: "Partner with local food influencers to drive authentic reach and footfall" },
];

const repeatSystem = [
  { icon: QrCode, label: "QR-Based Capture", desc: "Turn walk-ins into known customers" },
  { icon: MessageSquare, label: "WhatsApp Automation", desc: "Automated messages that bring them back" },
  { icon: RefreshCw, label: "Repeat Order Campaigns", desc: "Targeted offers to drive reorders" },
  { icon: Mail, label: "Re-engagement Flows", desc: "Win back lapsed customers automatically" },
];

const optimization = [
  { icon: BarChart, label: "Promo Performance Analysis", desc: "Understand which promotions actually drive orders and revenue" },
  { icon: UserCheck, label: "Repeat User Identification", desc: "Identify and segment your most loyal customers" },
  { icon: PieChart, label: "Campaign ROI Tracking", desc: "Real-time insights into what's working and what's not" },
  { icon: TrendingUp, label: "Revenue Optimization", desc: "Daily signal analysis to optimize spend and maximize returns" },
];

const caseStudies = [
  {
    id: "flaming-tikkas",
    client: "Flaming Tikkas",
    desc: "Multi-Location Restaurant",
    problem: "Low repeat customer rate across locations with inconsistent order volume and high dependency on aggregator platforms.",
    results: ["40% increase in repeat orders", "2.5x direct online orders", "Consistent daily order volume"],
  },
];

const whyItWorks = [
  { icon: Target, text: "We focus on orders and repeat revenue—not just followers" },
  { icon: Zap, text: "Content that converts, not just entertains" },
  { icon: BarChart3, text: "Full visibility from content to customer" },
  { icon: Shield, text: "Systems that bring customers back automatically" },
];

const SystemPillar = ({ title, items, delay = 0 }: { title: string; items: typeof contentEngine; delay?: number }) => (
  <motion.div
    className="p-6 rounded-xl bg-card border border-border"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay }}
  >
    <h3 className="text-xl font-bold text-foreground mb-6">{title}</h3>
    <div className="space-y-4">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <item.icon className="w-4 h-4 text-primary" />
          </div>
          <div>
            <p className="font-medium text-foreground text-sm">{item.label}</p>
            <p className="text-xs text-muted-foreground">{item.desc}</p>
          </div>
        </div>
      ))}
    </div>
  </motion.div>
);

const RestaurantsLanding = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <section className="py-24 px-4 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <UtensilsCrossed className="w-4 h-4" /> For Restaurants & QSR
            </motion.div>
            <motion.h1
              className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              We Help Restaurants Increase Orders and{" "}
              <span className="text-primary">Repeat Customers</span>
            </motion.h1>
            <motion.p
              className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Using content, ads, and automated follow-ups to build a loyal customer base.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Link to="/contact">
                <Button size="lg" className="text-base px-8 py-6 gap-2">
                  Get a Free Audit <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Problems */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Sound Familiar?
              </h2>
              <p className="text-muted-foreground text-lg">
                Most restaurants struggle with the same growth challenges.
              </p>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-5">
              {problems.map((p, i) => (
                <motion.div
                  key={i}
                  className="flex items-start gap-4 p-6 rounded-xl bg-destructive/5 border border-destructive/10"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                    <p.icon className="w-5 h-5 text-destructive" />
                  </div>
                  <p className="text-foreground font-medium pt-1.5">{p.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* System - 4 pillars */}
        <section className="py-20 px-4 bg-secondary">
          <div className="max-w-5xl mx-auto">
            <motion.div
              className="text-center mb-14"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Our Restaurant Growth System
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Content, distribution, repeat customer systems, and optimization working together.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              <SystemPillar title="1. Content Engine" items={contentEngine} delay={0} />
              <SystemPillar title="2. Distribution" items={distribution} delay={0.1} />
              <SystemPillar title="3. Repeat System" items={repeatSystem} delay={0.2} />
              <SystemPillar title="4. Optimization" items={optimization} delay={0.3} />
            </div>
          </div>
        </section>

        {/* Real Results */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <motion.h2
              className="text-3xl md:text-4xl font-bold text-foreground text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Real Results
            </motion.h2>
            <div className="max-w-lg mx-auto">
              {caseStudies.map((cs, i) => (
                <Link to={`/case-studies#${cs.id}`} key={i}>
                  <motion.div
                    className="p-6 rounded-xl bg-card border border-border hover:border-primary/30 hover:shadow-lg transition-all cursor-pointer group"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <UtensilsCrossed className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors">
                          {cs.client}
                        </h3>
                        <p className="text-xs text-muted-foreground">{cs.desc}</p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4 mt-3">{cs.problem}</p>
                    <div className="space-y-2">
                      {cs.results.map((r, j) => (
                        <div key={j} className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                          <span className="text-sm font-medium text-foreground">{r}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex items-center gap-1 text-sm text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      View full case study <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Why It Works */}
        <section className="py-20 px-4 bg-secondary">
          <div className="max-w-4xl mx-auto">
            <motion.h2
              className="text-3xl md:text-4xl font-bold text-foreground text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Why Our Approach Works
            </motion.h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {whyItWorks.map((item, i) => (
                <motion.div
                  key={i}
                  className="flex items-center gap-4 p-5 rounded-xl bg-card border border-border"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <p className="text-foreground font-medium">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 px-4 bg-primary text-primary-foreground">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Increase Orders and Build a Loyal Customer Base?
            </h2>
            <p className="text-primary-foreground/80 text-lg mb-8">
              Get a free audit and see exactly how to grow your restaurant's revenue.
            </p>
            <Link to="/contact">
              <Button size="lg" variant="secondary" className="text-base px-8 py-6 gap-2">
                Get a Free Audit <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default RestaurantsLanding;
